/**
 * OnboardingWizard
 * --------------------------------------------------------------------------
 * First-run modal for the pilot admin. Opens automatically when the company
 * profile has never been completed (GET /company/profile → completed=false).
 *
 * Steps:
 *   1. Company basics — name, operator type, country, regulator
 *   2. Base of operations — ICAO code + timezone (auto-detected)
 *   3. Contact & branding — email, phone, website, logo URL (optional)
 *   4. Review & finish
 *
 * The wizard cannot be dismissed until step 1 (name) is saved. Every step
 * PUTs to /company/profile so partial progress survives page reloads.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Building2, Plane, Mail, Sparkles, CheckCircle2, ChevronRight,
  ChevronLeft, Loader2, X, FileDown,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import TeamInviteWizard from '@/components/TeamInviteWizard';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const OPERATOR_TYPES = [
  { value: 'commercial_airline', label: 'Commercial Airline' },
  { value: 'mro', label: 'MRO / Maintenance Provider' },
  { value: 'ground_handler', label: 'Ground Handling' },
  { value: 'corporate', label: 'Corporate / Business Aviation' },
  { value: 'training', label: 'Training Organisation (ATO)' },
  { value: 'airport_operator', label: 'Airport Operator' },
  { value: 'other', label: 'Other' },
];

const REGULATORS = [
  { value: 'GACA', label: 'GACA (Saudi Arabia)' },
  { value: 'EASA', label: 'EASA (Europe)' },
  { value: 'FAA', label: 'FAA (USA)' },
  { value: 'CAAS', label: 'CAAS (Singapore)' },
  { value: 'CASA', label: 'CASA (Australia)' },
  { value: 'DGCA', label: 'DGCA (India)' },
  { value: 'ICAO', label: 'ICAO (Reference)' },
  { value: 'other', label: 'Other' },
];

const TOTAL_STEPS = 4;

export default function OnboardingWizard({ token, onComplete }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [checking, setChecking] = useState(true);
  const [saving, setSaving] = useState(false);
  const [finished, setFinished] = useState(false);         // shows the "Day-1 Report" success screen
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [profile, setProfile] = useState({
    name: '',
    legal_name: '',
    operator_type: 'commercial_airline',
    country: '',
    regulator: 'GACA',
    base_airport_icao: '',
    base_airport_name: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    contact_email: '',
    contact_phone: '',
    website: '',
    logo_url: '',
    aoc_number: '',
  });

  // ------- Load existing profile on mount -----------------------------
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/company/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) {
          const j = await r.json();
          setProfile((p) => ({ ...p, ...Object.fromEntries(Object.entries(j || {}).filter(([, v]) => v !== null && v !== undefined)) }));
          if (!j?.completed) setOpen(true);
        }
      } catch {
        /* silent — wizard just stays closed */
      } finally {
        setChecking(false);
      }
    })();
  }, [token]);

  const update = (patch) => setProfile((p) => ({ ...p, ...patch }));

  const saveDraft = useCallback(async (extra = {}) => {
    setSaving(true);
    try {
      const body = { ...profile, ...extra };
      const r = await fetch(`${API_BASE}/api/company/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error((await r.json()).detail || `HTTP ${r.status}`);
      return true;
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
      return false;
    } finally {
      setSaving(false);
    }
  }, [token, profile]);

  const next = async () => {
    // Step-specific validation
    if (step === 1 && !profile.name.trim()) {
      toast.warning('Please enter the company name.');
      return;
    }
    const ok = await saveDraft();
    if (ok) setStep((s) => Math.min(TOTAL_STEPS, s + 1));
  };

  const back = () => setStep((s) => Math.max(1, s - 1));

  const finish = async () => {
    const ok = await saveDraft({ completed: true });
    if (ok) {
      toast.success(`Welcome to AeroGuard, ${profile.name}!`, {
        description: 'Your workspace is ready. Grab the Day-1 Setup Report to share with your SMS committee.',
      });
      setFinished(true);
      onComplete?.(profile);
    }
  };

  const downloadKickoffPdf = async () => {
    setDownloadingPdf(true);
    try {
      const r = await fetch(`${API_BASE}/api/company/kickoff-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safe = (profile.name || 'AeroGuard').replace(/[^\w\- ]/g, '').replace(/\s+/g, '_');
      a.download = `${safe}_Day1_Setup_Report.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success('Day-1 Setup Report downloaded.');
    } catch (e) {
      toast.error(`Download failed: ${e.message}`);
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (checking) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { /* not dismissible on step 1 without a name */
      if (v || (step > 1 || profile.name.trim())) setOpen(v);
    }}>
      <DialogContent
        className="bg-slate-950 border-slate-800 text-slate-100 sm:max-w-2xl"
        data-testid="onboarding-wizard"
        onEscapeKeyDown={(e) => { if (step === 1 && !profile.name.trim()) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (step === 1 && !profile.name.trim()) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="w-5 h-5 text-violet-400" />
            Welcome — let&apos;s set up your company
          </DialogTitle>
          <div className="text-[12px] text-slate-400">
            Step {step} of {TOTAL_STEPS} · takes about 90 seconds
          </div>
          {/* Progress bar */}
          <div className="mt-2 h-1.5 bg-slate-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-violet-500 to-cyan-400 transition-all"
              style={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
            />
          </div>
        </DialogHeader>

        <div className="space-y-4 pt-2 min-h-[280px]" data-testid={`onboarding-step-${step}`}>
          {step === 1 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-cyan-300 text-sm">
                <Building2 className="w-4 h-4" /> Company basics
              </div>
              <div>
                <Label htmlFor="company-name">Company name *</Label>
                <Input
                  id="company-name"
                  data-testid="onboarding-company-name"
                  value={profile.name}
                  onChange={(e) => update({ name: e.target.value })}
                  placeholder="e.g. Saudia Cargo"
                  className="bg-slate-900 border-slate-700 mt-1"
                  autoFocus
                />
              </div>
              <div>
                <Label htmlFor="legal-name">Legal / registered name</Label>
                <Input
                  id="legal-name"
                  value={profile.legal_name}
                  onChange={(e) => update({ legal_name: e.target.value })}
                  placeholder="e.g. Saudia Cargo Company Ltd."
                  className="bg-slate-900 border-slate-700 mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Operator type</Label>
                  <Select value={profile.operator_type} onValueChange={(v) => update({ operator_type: v })}>
                    <SelectTrigger className="bg-slate-900 border-slate-700 mt-1" data-testid="onboarding-operator-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                      {OPERATOR_TYPES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Primary regulator</Label>
                  <Select value={profile.regulator} onValueChange={(v) => update({ regulator: v })}>
                    <SelectTrigger className="bg-slate-900 border-slate-700 mt-1" data-testid="onboarding-regulator">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                      {REGULATORS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Country of registration</Label>
                <Input
                  value={profile.country}
                  onChange={(e) => update({ country: e.target.value })}
                  placeholder="e.g. Saudi Arabia"
                  className="bg-slate-900 border-slate-700 mt-1"
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-cyan-300 text-sm">
                <Plane className="w-4 h-4" /> Base of operations
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Base airport (ICAO) *</Label>
                  <Input
                    value={profile.base_airport_icao}
                    onChange={(e) => update({ base_airport_icao: e.target.value.toUpperCase().slice(0, 4) })}
                    placeholder="OEJN"
                    className="bg-slate-900 border-slate-700 mt-1 font-mono"
                    data-testid="onboarding-icao"
                  />
                </div>
                <div>
                  <Label>Airport name</Label>
                  <Input
                    value={profile.base_airport_name}
                    onChange={(e) => update({ base_airport_name: e.target.value })}
                    placeholder="King Abdulaziz International"
                    className="bg-slate-900 border-slate-700 mt-1"
                  />
                </div>
              </div>
              <div>
                <Label>Timezone</Label>
                <Input
                  value={profile.timezone}
                  onChange={(e) => update({ timezone: e.target.value })}
                  placeholder="Asia/Riyadh"
                  className="bg-slate-900 border-slate-700 mt-1 font-mono text-xs"
                />
                <p className="text-[11px] text-slate-500 mt-1">
                  Auto-detected from your browser. Used for report timestamps and FRMS calculations.
                </p>
              </div>
              <div>
                <Label>Air Operator Certificate (AOC) number</Label>
                <Input
                  value={profile.aoc_number}
                  onChange={(e) => update({ aoc_number: e.target.value })}
                  placeholder="e.g. SA-AOC-0001 (optional)"
                  className="bg-slate-900 border-slate-700 mt-1"
                />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-cyan-300 text-sm">
                <Mail className="w-4 h-4" /> Contact & branding
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Safety contact email</Label>
                  <Input
                    type="email"
                    value={profile.contact_email}
                    onChange={(e) => update({ contact_email: e.target.value })}
                    placeholder="safety@example.com"
                    className="bg-slate-900 border-slate-700 mt-1"
                  />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={profile.contact_phone}
                    onChange={(e) => update({ contact_phone: e.target.value })}
                    placeholder="+966 …"
                    className="bg-slate-900 border-slate-700 mt-1"
                  />
                </div>
              </div>
              <div>
                <Label>Website</Label>
                <Input
                  value={profile.website}
                  onChange={(e) => update({ website: e.target.value })}
                  placeholder="https://…"
                  className="bg-slate-900 border-slate-700 mt-1"
                />
              </div>
              <div>
                <Label>Logo URL</Label>
                <Input
                  value={profile.logo_url}
                  onChange={(e) => update({ logo_url: e.target.value })}
                  placeholder="https://…/logo.png (optional)"
                  className="bg-slate-900 border-slate-700 mt-1"
                />
                {profile.logo_url && (
                  <img
                    src={profile.logo_url}
                    alt="Logo preview"
                    className="mt-2 h-10 max-w-[160px] object-contain bg-white/5 rounded p-1"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                )}
              </div>
              <p className="text-[11px] text-slate-500">
                All fields here are optional. You can add or edit them later in <b>Settings → Company Profile</b>.
              </p>
            </div>
          )}

          {step === 4 && !finished && (
            <div className="space-y-3" data-testid="onboarding-review">
              <div className="flex items-center gap-2 text-emerald-300 text-sm">
                <CheckCircle2 className="w-4 h-4" /> Review & finish
              </div>
              <div className="rounded-md border border-slate-800 bg-slate-900/60 p-3 text-[12px] space-y-1.5">
                <div><span className="text-slate-500">Name:</span> <b className="text-slate-100">{profile.name || '—'}</b></div>
                <div><span className="text-slate-500">Operator:</span> <b className="text-slate-200">{OPERATOR_TYPES.find(o => o.value === profile.operator_type)?.label || '—'}</b></div>
                <div><span className="text-slate-500">Regulator:</span> <b className="text-slate-200">{profile.regulator || '—'}</b></div>
                <div><span className="text-slate-500">Base airport:</span> <b className="text-slate-200 font-mono">{profile.base_airport_icao || '—'}</b> {profile.base_airport_name && `— ${profile.base_airport_name}`}</div>
                <div><span className="text-slate-500">Timezone:</span> <b className="text-slate-200 font-mono">{profile.timezone}</b></div>
                <div><span className="text-slate-500">Contact:</span> <b className="text-slate-200">{profile.contact_email || '—'}</b></div>
              </div>
              <div className="rounded-md border border-cyan-500/30 bg-cyan-500/5 p-3 text-[12px] text-cyan-100">
                Once you finish, this dialog won&apos;t appear again. Your operational data starts empty — invite your safety team and begin logging reports.
              </div>
            </div>
          )}

          {finished && (
            <div className="space-y-4" data-testid="onboarding-finished">
              <div className="flex items-center gap-2 text-emerald-300">
                <CheckCircle2 className="w-5 h-5" />
                <span className="text-sm font-semibold">Setup complete — welcome aboard, {profile.name || 'Captain'}!</span>
              </div>
              <div className="rounded-md border border-violet-500/30 bg-violet-500/5 p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <FileDown className="w-5 h-5 text-violet-300 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-slate-100 text-sm font-semibold">Your Day-1 Setup Report is ready</div>
                    <div className="text-[12px] text-slate-400 mt-0.5">
                      One-page PDF you can hand to your Accountable Executive and SMS committee.
                      Covers what&apos;s enabled today, your first-7-days checklist, and a signature block.
                    </div>
                  </div>
                </div>
                <Button
                  onClick={downloadKickoffPdf}
                  disabled={downloadingPdf}
                  className="w-full bg-violet-600 hover:bg-violet-700"
                  data-testid="onboarding-download-kickoff-pdf"
                >
                  {downloadingPdf
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Preparing PDF…</>
                    : <><FileDown className="w-4 h-4 mr-2" /> Download Day-1 Setup Report</>}
                </Button>
              </div>
              <div className="rounded-md border border-cyan-500/30 bg-cyan-500/5 p-4 space-y-3">
                <div className="text-slate-100 text-sm font-semibold">Invite your team now</div>
                <div className="text-[12px] text-slate-400">
                  Add your Safety Manager, Auditor, and crew in bulk — CSV upload or one-by-one.
                  Each person gets a temporary password to change on first login.
                </div>
                <TeamInviteWizard
                  token={token}
                  trigger={
                    <Button className="w-full bg-cyan-600 hover:bg-cyan-700" data-testid="onboarding-invite-team">
                      Open team invite wizard
                    </Button>
                  }
                />
              </div>
              <p className="text-[11px] text-slate-500 text-center">
                You can regenerate the PDF and invite more people anytime from Admin Data Management.
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-slate-800">
          {finished ? (
            <>
              <span className="text-[11px] text-slate-500">
                Your workspace is ready. Explore the sidebar to invite your team.
              </span>
              <Button
                onClick={() => setOpen(false)}
                className="bg-slate-800 hover:bg-slate-700 text-slate-100 ml-auto"
                data-testid="onboarding-close"
              >
                Enter AeroGuard →
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                onClick={back}
                disabled={step === 1 || saving}
                className="text-slate-400"
                data-testid="onboarding-back"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              {step < TOTAL_STEPS ? (
                <Button
                  onClick={next}
                  disabled={saving}
                  className="bg-violet-600 hover:bg-violet-700"
                  data-testid="onboarding-next"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button
                  onClick={finish}
                  disabled={saving}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="onboarding-finish"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                  Finish setup
                </Button>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
